import { useCallback, useMemo, useRef } from 'react'
import { CONST } from '../../constants.ts'
import { useUrlSearchParams } from './useUrlSearchParams.ts'

// #region Types
// =============================================================================

/**
 * Standard UTM (Urchin Tracking Module) parameters used to attribute traffic
 * to marketing campaigns in analytics tools.
 *
 * See more:
 *   - {@link https://support.google.com/analytics/answer/10917952}
 *   - {@link https://en.wikipedia.org/wiki/UTM_parameters}
 */
export type UTMParameters = {
	/** Unique identifier for a specific ad campaign (e.g., `abc.123`). */
	utm_id?: string
	/** Referrer that drove the traffic (e.g., `google`, `newsletter`, `twitter`). */
	utm_source?: string
	/** Marketing medium or channel (e.g., `cpc`, `email`, `social`, `banner`). */
	utm_medium?: string
	/** Name of the campaign, product, or promotion (e.g., `spring_sale`). */
	utm_campaign?: string
	/** Paid search keyword or audience term targeted by the campaign. */
	utm_term?: string
	/** Differentiates similar links/content within the same campaign (e.g., `logolink` vs `textlink`). */
	utm_content?: string
}

// #endregion

// #region Constants
// =============================================================================

const UTM_PARAM_KEYS = [
	'utm_id',
	'utm_source',
	'utm_medium',
	'utm_campaign',
	'utm_term',
	'utm_content',
] as const satisfies (keyof UTMParameters)[]

// #endregion

// #region Helper functions
// =============================================================================

/**
 * Type guard that narrows a value to a `[string, string]` tuple. Used to
 * filter out `[key, null]` entries when collecting UTM params.
 */
function isStringTuple(item: unknown): item is [string, string] {
	return (
		Array.isArray(item) &&
		item.length === 2 &&
		typeof item[0] === 'string' &&
		typeof item[1] === 'string'
	)
}

/**
 * Returns a function that yields a reference-stable `URL` across renders,
 * reusing the previous instance when the stringified URL is unchanged so the
 * result is safe to use in dependency arrays.
 */
function useStabilizeUrl() {
	const urlRef = useRef<URL | undefined>(undefined)
	return useCallback((newUrl: URL): URL => {
		if (newUrl.toString() !== urlRef.current?.toString()) {
			urlRef.current = newUrl
		}
		return urlRef.current
	}, [])
}

/**
 * Reads UTM parameters from the current page URL's query string and returns
 * them as a `UTMParameters` object, or `undefined` if none are present.
 *
 * Note: If the same key has multiple values, the last value will win.
 */
function useCurrentUtmParams(): UTMParameters | undefined {
	const searchParams = useUrlSearchParams()
	return useMemo(() => {
		const utmParams = Object.fromEntries(
			UTM_PARAM_KEYS.map((key) => [key, searchParams.get(key)]).filter(
				isStringTuple,
			),
		) as UTMParameters

		const hasUtmParams = Object.values(utmParams).some((value) =>
			Boolean(value),
		)
		return hasUtmParams ? utmParams : undefined
	}, [searchParams])
}

// #endregion

// #region Main hook
// =============================================================================

/**
 * Controls how UTM parameters are sourced when building a URL with
 * `useUrlWithUtmParameters`.
 */
export interface UseUrlWithUtmParametersOptions {
	/**
	 * When `true`, UTM parameters from the current page URL are forwarded onto
	 * the returned URL (campaign attribution is preserved across links) and
	 * `utmParams` is ignored. If the current page has no UTM parameters,
	 * `utmParams` is used as a fallback.
	 */
	preserveUtmParams?: boolean
	/**
	 * UTM parameters to apply to the returned URL. Ignored when
	 * `preserveUtmParams` is `true` and the current page already carries UTM
	 * parameters.
	 */
	utmParams?: UTMParameters
}

/**
 * Builds an absolute URL from `baseUrl` with UTM parameters applied.
 *
 * Resolution of UTM values:
 * - If `options.preserveUtmParams` is `true` and the current page URL contains
 *   any UTM parameters, those are forwarded onto the returned URL and
 *   `options.utmParams` is ignored.
 *   - Note: if a utm parameter has multiple values, only the last value will be
 *     preserved. (UTM does not support multi-valued parameters).
 * - Otherwise, if `options.utmParams` is provided, those entries are applied
 *   to the returned URL.
 *
 * The returned URL is reference-stable across renders as long as the
 * stringified URL is unchanged, making it safe to use in dependency arrays.
 *
 * NOTE: `baseUrl` should not have any UTM parameters of its own — they will be
 * overridden.
 *
 * @param baseUrl - The target URL (absolute or relative) to decorate.
 * @param options - Controls how UTM parameters are sourced.
 * @returns A reference-stable URL with UTM parameters applied.
 */
export function useUrlWithUtmParameters(
	baseUrl: string | URL,
	{ preserveUtmParams, utmParams }: UseUrlWithUtmParametersOptions,
): URL {
	const currentUtmParams = useCurrentUtmParams()
	const stabilize = useStabilizeUrl()

	const base =
		typeof window !== 'undefined'
			? window.location.href
			: `https://${CONST.APP_DOMAIN}`

	const getUrl = (params: UTMParameters = {}) => {
		const newUrl = new URL(baseUrl, base)
		Object.entries(params).forEach(([key, value]) => {
			newUrl.searchParams.set(key, value)
		})
		return stabilize(newUrl)
	}

	if (preserveUtmParams && currentUtmParams) {
		return getUrl(currentUtmParams)
	}

	return getUrl(utmParams)
}

// #endregion
